import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

export const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

export const getFilterWrapperStyles = ({ isDisabled }) => ({
  height: 'max-content',
  borderRadius: '8px',
  backgroundColor: ({ palette }) =>
    isDisabled ? palette.background.secondary : palette.background.primary,
  color: ({ palette }) => palette.text.primary,
  transition: 'width 0.5s',
  ...getConditionalObject(!isDisabled, {
    '&:hover': {
      backgroundColor: ({ palette }) => palette.background.secondary,
    },
  }),
});

export const getSelectedFilterWrapperStyles = ({ isDisabled }) => ({
  backgroundColor: ({ palette }) => palette.primary.main,
  color: ({ palette }) => palette.background.primary,
  ...getConditionalObject(!isDisabled, {
    '&:hover': {
      backgroundColor: COLOR_PALETTE.BRAND[7],
    },
  }),
});

export const getErrorFilterWrapperStyles = ({ isDisabled }) => ({
  backgroundColor: ({ palette }) => palette.error.main,
  color: ({ palette }) => palette.background.primary,
  ...getConditionalObject(!isDisabled, {
    '&:hover': {
      backgroundColor: COLOR_PALETTE.RED[7],
    },
  }),
});

export const getTextFieldStyles = ({ isDisabled, isSelected, isError }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 40,
    px: 2,
    py: 0.75,
    paddingRight: '11px',
    borderRadius: '8px',
    borderColor: ({ palette }) => palette.border.filters,
    color: 'inherit',
    backgroundColor: ({ palette }) => {
      if (!isSelected) {
        return palette.background.primary;
      }

      return isError ? palette.error.main : palette.primary.main;
    },
    ...getConditionalObject(!isDisabled, {
      '&:hover': {
        backgroundColor: ({ palette }) => {
          if (!isSelected) {
            return palette.background.secondary;
          }

          return isError ? palette.error.dark : palette.primary.dark;
        },
      },
    }),
    '&:focus, &:focus-visible, &.Mui-focused, & .Mui-focused': {
      borderColor: ({ palette }) => palette.border.filters,
      boxShadow: ({ palette }) => `0px 0px 0px 2px ${palette.border.default}`,
    },
    '& .MuiOutlinedInput-notchedOutline:hover ': {
      borderColor: ({ palette }) => palette.border.filters,
    },
  },

  '& input.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd': {
    color: ({ palette }) =>
      `${isSelected ? palette.background.primary : palette.text.primary} !important`,
    padding: '0 !important',
  },

  '& .MuiInputBase-root': {
    cursor: isDisabled ? 'default' : 'pointer',

    '&:hover.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: ({ palette }) => palette.border.filters,
    },
  },

  '& .MuiInputBase-input': {
    p: 0,
    cursor: isDisabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export const inputLabelStyles = {
  '&.MuiFormLabel-root, &.MuiFormLabel-root.Mui-focused': {
    top: '-6px',
    color: ({ palette }) => palette.text.primary,
  },
};

export const iconButtonStyles = {
  color: ({ palette }) => palette.text.primary,

  '& .selected-icon': {
    borderRadius: '50%',
    color: ({ palette }) => palette.background.primary,
  },

  '&:hover': {
    backgroundColor: 'transparent',
  },
};

export const inputStyles = {
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '24px',

  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ({ palette }) => palette.border.filters,
  },
};

export const filterMenuStyles = {
  px: 0,
  py: 2,
};

export const popoverSlotProps = {
  root: {
    sx: {
      marginTop: '8px',
    },
  },
  paper: {
    sx: {
      borderRadius: '8px',
      boxShadow: ({ palette }) => palette.shadow.s,
    },
  },
};

export const menuListStyles = {
  maxHeight: 168,
  overflowY: 'auto',
};

export const menuControlStyles = {
  flexDirection: 'row',
  gap: 1,
  alignSelf: 'flex-end',
  p: 2,
  paddingBottom: 0,
};

export const dateRangeMenuControlStyles = {
  flexDirection: 'row',
  gap: 1,
  alignSelf: 'flex-start',
  p: 2,
  paddingBottom: 0,
};

export const dateRangeContentStyles = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  px: 2,
  pt: 2,
};

export const dateRangeSeparatorStyles = {
  mx: 0.5,
};

export const dateTimezoneSwitchStyles = {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0.5,
  p: 2,
};

export const dateRangeFieldStyles = {
  width: '160px',
  '& .MuiInputBase-root': {
    height: '40px',
  },
};

export const getArrowIconStyles = ({ isTextFilter, isSelectedValues, isClearable }) => ({
  display: 'flex',
  ...getConditionalObject(isTextFilter, { visibility: 'hidden' }),
  ...getConditionalObject(isSelectedValues && !isClearable, {
    color: ({ palette }) => palette.background.primary,
  }),
});

export const optionCheckboxStyles = {
  padding: 0,
  // The theme pins the indeterminate checkbox to 20px, which clips the icon margin and shifts the label.
  '&.MuiCheckbox-indeterminate': {
    width: 'auto',
    height: 'auto',
  },
  '& .MuiSvgIcon-root': {
    fill: ({ palette }) => palette.border.filters,
    mr: 1,
  },
  '&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root': {
    fill: ({ palette }) => palette.primary.main,
  },
};
